import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import CookieBanner from "components/CookieBanner";
import NotFound from "pages/NotFound";
import LoginForm from './pages/login-form';
import LandingPage from './pages/landing-page';
import SignupForm from './pages/signup-form';
import AuthCallback from 'pages/auth-callback';
import ProfessionalRegistrationStep2 from 'pages/professional-registration-step-2';
import ProfessionalDashboard from 'pages/professional-dashboard';
import ClientRegistrationPage from 'pages/client-registration-page';
import ClientDashboard from 'pages/client-dashboard';
import NewServiceRequestPage from 'pages/new-service-request-page';
import PaymentVerificationPage from 'pages/payment-verification-page';
import ServiceRequestDetailPage from 'pages/service-request-detail-page';
import PrivacyPolicyPage from 'pages/privacy-policy-page';
import TermsAndConditionsPage from 'pages/terms-and-conditions-page';
import CookiePolicyPage from 'pages/cookie-policy-page';
import FAQPage from 'pages/faq-page';
import AdminDashboardOverview from 'pages/admin-dashboard-overview';
import AdminClientsManagement from 'pages/admin-clients-management';
import AdminProfessionistiManagement from 'pages/admin-professionisti-management';
import AdminPendingProfessionalsPage from 'pages/admin-pending-professionals-page';
import AdminAuditLog from 'pages/admin-audit-log';
import AdminRichiestePage from 'pages/admin-richieste';
import AdminPreventiviPage from 'pages/admin-preventivi';
import AdminSettings from 'pages/admin-settings';
import AdminPagamentiRateali from 'pages/admin-pagamenti-rateali';
import AdminRequestDetail from 'pages/admin-request-detail';
import AdminFatturazione from 'pages/admin-fatturazione';
import AdminReviewsManagement from 'pages/admin-reviews-management';
import AdminPublicPagesManagement from 'pages/admin-public-pages-management';
import AdminInsolvenzeManagement from 'pages/admin-insolvenze-management';
import ProfessionalVerificationReviewPage from 'pages/professional-verification-review-page';
import AdminLandingPageManagement from 'pages/admin-landing-page-management';
import AdminProgrammaVip from 'pages/admin-programma-vip';
import AdminCategorie from 'pages/admin-categorie';
import ProtectedRoute from 'components/ProtectedRoute';
import ProfessionalRequestDetailPage from 'pages/professional-request-detail-page';
import SendQuotePage from 'pages/send-quote-page';
import ResetPassword from 'pages/reset-password';
import UpdatePassword from 'pages/update-password';
import ProfessionalTrattative from 'pages/professional-trattative';
import ComeFunzionaProfessionistiPage from 'pages/come-funziona-professionisti';
import ComeFunzionaClientiPage from 'pages/come-funziona-clienti';
import ProfessionalSettings from 'pages/professional-settings';
import ClienteRichiesteArchiviate from 'pages/cliente-richieste-archiviate';
import ClienteSettings from 'pages/cliente-settings';
import ClienteCheckoutPage from 'pages/cliente-checkout';
import ClienteCheckoutConfermaPage from 'pages/cliente-checkout-conferma';
import ProfessionalPaginaPubblica from 'pages/professional-pagina-pubblica';
import ProfiloPubblico from 'pages/profilo-pubblico';
import ReviewPage from 'pages/review-page';
import ProfessionalRecensioni from 'pages/professional-recensioni';
import ProfessionalLavoriConfermati from 'pages/professional-lavori-confermati';
import ProfessionalCrediti from 'pages/professional-crediti';

import { AuthProvider } from 'contexts/AuthContext';
import AdminRoute from './components/AdminRoute';
import { useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import { loadGoogleAnalytics, trackPageView } from './lib/analytics';
import { useAuth } from 'contexts/AuthContext';

// ─── GA_MEASUREMENT_ID localStorage key (mirrors CookieBanner's LS_KEY) ───────
const CONSENT_LS_KEY = 'volaptic_cookie_consent';

/**
 * Inner component — must live inside BrowserRouter + AuthProvider so it can
 * access useLocation and useAuth.
 */
const AnalyticsManager = () => {
  const location = useLocation();
  const { user } = useAuth();
  const measurementIdRef = useRef(null);
  const gaReadyRef = useRef(false);

  // ── Helper: read current consent state (same logic as CookieBanner) ──────────
  const getConsentState = async (userId, bannerVersion) => {
    if (userId) {
      try {
        const { data, error } = await supabase
          ?.from('cookie_consent_log')
          ?.select('consent_choice, banner_version')
          ?.eq('user_id', userId)
          ?.order('created_at', { ascending: false })
          ?.limit(1)
          ?.maybeSingle();

        if (error || !data) return null;
        // Only honour a consent that matches the current banner version
        if (bannerVersion && data?.banner_version !== bannerVersion) return null;
        return data?.consent_choice ?? null;
      } catch {
        return null;
      }
    } else {
      try {
        const stored = JSON.parse(localStorage.getItem(CONSENT_LS_KEY) || 'null');
        if (!stored) return null;
        if (bannerVersion && stored?.version !== bannerVersion) return null;
        return stored?.choice ?? null;
      } catch {
        return null;
      }
    }
  };

  // ── Attempt to initialise GA (called on mount and on consent change) ─────────
  const tryInitGA = async () => {
    const measurementId = measurementIdRef?.current;
    if (!measurementId || gaReadyRef?.current) return;

    // Fetch the current banner version so consent comparison is version-aware
    let bannerVersion = null;
    try {
      const { data } = await supabase
        ?.from('platform_config')
        ?.select('value')
        ?.eq('key', 'cookie_banner_version')
        ?.maybeSingle();
      bannerVersion = data?.value ?? null;
    } catch {
      // non-fatal — proceed without version check
    }

    const consent = await getConsentState(user?.id ?? null, bannerVersion);
    if (consent === 'accepted') {
      loadGoogleAnalytics(measurementId);
      gaReadyRef.current = true;
      // Fire the first pageview immediately after GA loads
      trackPageView(measurementId, location?.pathname + location?.search, document.title);
    }
  };

  // ── On mount: fetch ga_measurement_id from platform_config ──────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const { data, error } = await supabase
          ?.from('platform_config')
          ?.select('value')
          ?.eq('key', 'ga_measurement_id')
          ?.maybeSingle();

        if (error || !data?.value) return; // GA not configured — do nothing
        const id = String(data?.value)?.trim();
        if (!id) return;

        measurementIdRef.current = id;
        await tryInitGA();
      } catch {
        // non-fatal
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-check consent whenever user auth state changes (login/logout) ─────────
  useEffect(() => {
    if (measurementIdRef?.current && !gaReadyRef?.current) {
      tryInitGA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Listen for consent acceptance dispatched by CookieBanner ─────────────────
  // CookieBanner writes to localStorage and calls logConsent; we listen for a
  // storage event (anonymous) or a custom DOM event for same-tab acceptance.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e?.key !== CONSENT_LS_KEY) return;
      if (measurementIdRef?.current && !gaReadyRef?.current) {
        tryInitGA();
      }
    };

    const handleConsentAccepted = () => {
      if (measurementIdRef?.current && !gaReadyRef?.current) {
        tryInitGA();
      }
    };

    // storage fires in OTHER tabs; for same-tab we use a custom event
    window.addEventListener('storage', handleStorage);
    window.addEventListener('volaptic:consent_accepted', handleConsentAccepted);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('volaptic:consent_accepted', handleConsentAccepted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Track pageview on every route change ─────────────────────────────────────
  useEffect(() => {
    if (!gaReadyRef?.current || !measurementIdRef?.current) return;
    // Small timeout so document.title has updated for the new route
    const t = setTimeout(() => {
      trackPageView(measurementIdRef?.current, location?.pathname + location?.search, document.title);
    }, 0);
    return () => clearTimeout(t);
  }, [location]);

  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
        <ScrollToTop />
        <CookieBanner />
        <AnalyticsManager />
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login-form" element={<LoginForm />} />
          <Route path="/signup-form" element={<SignupForm />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/professional-registration-step-2" element={<ProfessionalRegistrationStep2 />} />
          <Route path="/professional-dashboard" element={<ProfessionalDashboard />} />
          <Route path="/professional/trattative" element={<ProtectedRoute><ProfessionalTrattative /></ProtectedRoute>} />
          <Route path="/professional/settings" element={<ProtectedRoute><ProfessionalSettings /></ProtectedRoute>} />
          <Route path="/professional/pagina-pubblica" element={<ProtectedRoute><ProfessionalPaginaPubblica /></ProtectedRoute>} />
          <Route path="/professional/recensioni" element={<ProtectedRoute><ProfessionalRecensioni /></ProtectedRoute>} />
          <Route path="/professional/lavori-confermati" element={<ProfessionalLavoriConfermati />} />
          <Route path="/professional/crediti" element={<ProtectedRoute><ProfessionalCrediti /></ProtectedRoute>} />
          <Route path="/p/:slug" element={<ProfiloPubblico />} />
          <Route path="/review/:token" element={<ReviewPage />} />
          <Route path="/request-detail/:requestId" element={<ProfessionalRequestDetailPage />} />
          <Route path="/send-quote/:requestId" element={<SendQuotePage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/registrati-cliente" element={<ClientRegistrationPage />} />
          <Route path="/cliente/dashboard" element={<ClientDashboard />} />
          <Route path="/cliente/richiesta/nuova" element={<NewServiceRequestPage />} />
          <Route path="/cliente/richieste/archiviate" element={<ClienteRichiesteArchiviate />} />
          <Route path="/cliente/settings" element={<ProtectedRoute><ClienteSettings /></ProtectedRoute>} />
          <Route path="/cliente/richieste/:requestId" element={<ServiceRequestDetailPage />} />
          <Route path="/cliente/checkout" element={<ProtectedRoute><ClienteCheckoutPage /></ProtectedRoute>} />
          <Route path="/cliente/checkout/conferma" element={<ClienteCheckoutConfermaPage />} />
          <Route path="/payment-verification" element={<PaymentVerificationPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/come-funziona-professionisti" element={<ComeFunzionaProfessionistiPage />} />
          <Route path="/come-funziona-clienti" element={<ComeFunzionaClientiPage />} />
          {/* Admin Routes - Protected */}
          <Route path="/admin" element={<AdminRoute><AdminDashboardOverview /></AdminRoute>} />
          <Route path="/admin/clienti" element={<AdminRoute><AdminClientsManagement /></AdminRoute>} />
          <Route path="/admin/professionisti" element={<AdminRoute><AdminProfessionistiManagement /></AdminRoute>} />
          <Route path="/admin/professionisti/pending" element={<AdminRoute><AdminPendingProfessionalsPage /></AdminRoute>} />
          <Route path="/admin/professionisti/review/:id" element={<AdminRoute><ProfessionalVerificationReviewPage /></AdminRoute>} />
          <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
          <Route path="/admin/richieste" element={<AdminRoute><AdminRichiestePage /></AdminRoute>} />
          <Route path="/admin/richieste/:requestId" element={<AdminRoute><AdminRequestDetail /></AdminRoute>} />
          <Route path="/admin/preventivi" element={<AdminRoute><AdminPreventiviPage /></AdminRoute>} />
          <Route path="/admin/recensioni" element={<AdminRoute><AdminReviewsManagement /></AdminRoute>} />
          <Route path="/admin/pagine-professionisti" element={<AdminRoute><AdminPublicPagesManagement /></AdminRoute>} />
          <Route path="/admin/fatturazione" element={<AdminRoute><AdminFatturazione /></AdminRoute>} />
          <Route path="/admin/insolvenze" element={<AdminRoute><AdminInsolvenzeManagement /></AdminRoute>} />
          <Route path="/admin/programma-vip" element={<AdminRoute><AdminProgrammaVip /></AdminRoute>} />
          <Route path="/admin/categorie" element={<AdminRoute><AdminCategorie /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
          <Route path="/admin/pagamenti-rateali" element={<AdminRoute><AdminPagamentiRateali /></AdminRoute>} />
          <Route path="/admin/landing-page" element={<AdminRoute><AdminLandingPageManagement /></AdminRoute>} />
          
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
